import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { useCoachClients, useCreateWorkspace, useInviteClient } from "@/lib/coachQueries";

/**
 * Master v5 Prompt 16 — topbar "+New". Both actions land on a REAL
 * existing capability rather than a fake/placeholder form: engagement
 * creation calls the existing coach/workspaces endpoint; "new feedback"
 * jumps straight into the existing per-client Feedback page's own
 * compose flow (Prompt 12) once a client is picked.
 * Prompt 30 adds "Invite new client" — a real Resend email invite, for
 * a client who has no portal account at all yet.
 */
export function NewMenu() {
  const [engagementOpen, setEngagementOpen] = useState(false);
  const [pickClientOpen, setPickClientOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" data-testid="coach-new-menu-trigger">
            <Plus size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">New</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid="coach-new-menu">
          <DropdownMenuItem onClick={() => setInviteOpen(true)} data-testid="coach-new-menu-invite-client">
            Invite new client
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setEngagementOpen(true)} data-testid="coach-new-menu-engagement">
            New client engagement
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setPickClientOpen(true)} data-testid="coach-new-menu-feedback">
            New feedback
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <InviteClientDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <NewEngagementDialog open={engagementOpen} onOpenChange={setEngagementOpen} />
      <PickClientDialog open={pickClientOpen} onOpenChange={setPickClientOpen} destination="feedback" />
    </>
  );
}

function InviteClientDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", title: "", programme_type: "presentation" });
  const [result, setResult] = useState(null);
  const inviteClient = useInviteClient();

  async function handleSubmit() {
    try {
      const res = await inviteClient.mutateAsync(form);
      setResult(res);
    } catch { /* surfaced below via inviteClient.isError */ }
  }

  function close(open) {
    onOpenChange(open);
    if (!open) { setResult(null); setForm({ client_name: "", client_email: "", title: "", programme_type: "presentation" }); }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="coach-invite-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-ink">Invite new client</DialogTitle>
          <DialogDescription>Sends them an email to set their password — their Workspace is created the moment they accept.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3">
            <p className="text-sm text-emerald-700" data-testid="coach-invite-client-success">
              {result.mode === "engagement_created"
                ? "This email already has an account — the engagement was created immediately."
                : `Invite created for ${result.email} — the email is on its way.`}
            </p>
            <Button onClick={() => close(false)} className="w-full" data-testid="coach-invite-client-done">Done</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <input placeholder="Client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="coach-invite-client-name" />
            <input placeholder="Client email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="coach-invite-client-email" />
            <input placeholder="Engagement title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="coach-invite-client-title" />
            <select value={form.programme_type} onChange={(e) => setForm({ ...form, programme_type: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="coach-invite-client-programme-type">
              <option value="presentation">Presentation</option>
              <option value="executive_presence">Executive presence</option>
            </select>
            {inviteClient.isError && <p className="text-sm text-red-600" data-testid="coach-invite-client-error">{inviteClient.error.body?.detail || "Couldn't send the invite."}</p>}
            <Button
              onClick={handleSubmit}
              disabled={!form.client_name || !form.client_email || !form.title || inviteClient.isPending}
              className="w-full" data-testid="coach-invite-client-submit"
            >
              {inviteClient.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Send invite
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewEngagementDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ title: "", client_emails: "", programme_type: "presentation", access_type: "individual" });
  const createWorkspace = useCreateWorkspace();
  const navigate = useNavigate();

  async function handleSubmit() {
    try {
      const result = await createWorkspace.mutateAsync({
        title: form.title, programme_type: form.programme_type, access_type: form.access_type,
        client_emails: form.client_emails.split(",").map((e) => e.trim()).filter(Boolean),
      });
      onOpenChange(false);
      navigate(`/rehearsal-room/w/${result.wrk_id}/home`);
    } catch { /* surfaced below via createWorkspace.isError */ }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="coach-new-engagement-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-ink">New client engagement</DialogTitle>
          <DialogDescription>Creates a new Workspace and invites the client(s).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input placeholder="Engagement title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="coach-new-engagement-title" />
          <input placeholder="Client email(s), comma separated" value={form.client_emails} onChange={(e) => setForm({ ...form, client_emails: e.target.value })} className="w-full border border-ink/15 rounded px-3 py-2 text-sm" data-testid="coach-new-engagement-emails" />
          <div className="flex gap-3">
            <select value={form.programme_type} onChange={(e) => setForm({ ...form, programme_type: e.target.value })} className="border border-ink/15 rounded px-3 py-2 text-sm flex-1" data-testid="coach-new-engagement-programme-type">
              <option value="presentation">Presentation</option>
              <option value="executive_presence">Executive presence</option>
            </select>
            <select value={form.access_type} onChange={(e) => setForm({ ...form, access_type: e.target.value })} className="border border-ink/15 rounded px-3 py-2 text-sm flex-1" data-testid="coach-new-engagement-access-type">
              <option value="individual">Individual</option>
              <option value="team">Team</option>
            </select>
          </div>
          {createWorkspace.isError && <p className="text-sm text-red-600" data-testid="coach-new-engagement-error">{createWorkspace.error.body?.detail || "Couldn't create the engagement."}</p>}
          <Button onClick={handleSubmit} disabled={!form.title || !form.client_emails || createWorkspace.isPending} className="w-full" data-testid="coach-new-engagement-submit">
            {createWorkspace.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Create engagement
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PickClientDialog({ open, onOpenChange, destination }) {
  const { data } = useCoachClients();
  const navigate = useNavigate();
  const items = data?.items || [];

  function go(item) {
    onOpenChange(false);
    navigate(`/rehearsal-room/w/${item.workspace_id}/${destination}`);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="coach-pick-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-ink">Choose a client</DialogTitle>
          <DialogDescription>Opens their {destination} page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-ink-soft" data-testid="coach-pick-client-empty">No clients yet.</p>
          ) : (
            items.map((item) => (
              <button
                key={item.member_id}
                onClick={() => go(item)}
                className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-ink/5"
                data-testid={`coach-pick-client-${item.member_id}`}
              >
                {item.member_name} <span className="text-ink-soft">— {item.workspace_title}</span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
